import { l as lock, u as unlock } from "./tua-bsl-uIxvG2ZV.js";
import { c as removeEmptyKeys, e as isMobile } from "./utils-D8tYCXRD.js";
class SearchAPIQueryHandler {
  constructor(config) {
    this.config = config;
  }
  async executeSearch(searchQuery) {
    if (!searchQuery || typeof searchQuery !== "string" || searchQuery.length < 3) {
      throw new Error(
        "Search query must be a non-empty string with at least 3 characters."
      );
    }
    const searchUrl = this.getSearchUrl(searchQuery);
    try {
      const response = await fetch(searchUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const searchResponse = await response.json();
      const searchResults = removeEmptyKeys(searchResponse.resources.results);
      return searchResults;
    } catch (error) {
      console.error("Error fetching data: ", error);
      throw error;
    }
  }
  getSearchUrl(searchQuery) {
    let searchUrl = `/search/suggest.json?q=${searchQuery}`;
    const { resources, limit, unavailable_products, fields } = this.config.SearchAPISearchOptions;
    if (resources) {
      searchUrl += `&resources[type]=${resources.join(",")}`;
    }
    if (limit) {
      searchUrl += `&resources[limit]=${limit}`;
    }
    if (unavailable_products) {
      searchUrl += `&resources[unavailable_products]=${unavailable_products}`;
    }
    if (fields) {
      searchUrl += `&resources[fields]=${fields.join(",")}`;
    }
    return searchUrl;
  }
}
class SearchInput extends HTMLElement {
  constructor() {
    super();
    this.searchButton = null;
    this.closeButton = null;
    this.viewAllButton = null;
    this.waitingResults = null;
    this.hasResults = null;
    this.noResults = null;
    this.suggestionColumn = null;
    this.collectionColumn = null;
    this.productColumn = null;
    this.articleColumn = null;
    this.overlay = null;
    this.headerSearchButton = null;
    this.apiConfig = {
      SearchAPISearchOptions: {
        resources: ["product"],
        unavailable_products: "hide",
        fields: ["title", "product_type", "variants.title", "vendor"],
        limit: 4
      }
    };
    this.searchHandler = new SearchAPIQueryHandler(this.apiConfig);
  }
  connectedCallback() {
    this.cacheDomElements();
    this.setEventListeners();
    this.setInitialValueFromUrl();
  }
  cacheDomElements() {
    this.searchInput = this.querySelector("[js-search-input='input']");
    if (!this.searchInput) {
      throw new Error("search input is a required element");
    }
    this.searchButton = this.querySelector("[js-search-input='button']");
    this.closeButton = this.querySelector("[js-search-input='icon-close']");
    this.viewAllButton = this.querySelector("[js-instant-search='view-all']");
    this.resultsParent = this.querySelector(
      "[js-instant-search='results-parent']"
    );
    this.waitingResults = this.querySelector(
      '[js-instant-search="waiting-input"]'
    );
    this.hasResults = this.querySelector('[js-instant-search="has-results"]');
    this.noResults = this.querySelector('[js-instant-search="no-results"]');
    this.suggestionColumn = this.querySelector(
      "[js-instant-search='suggestions']"
    );
    this.collectionColumn = this.querySelector(
      "[js-instant-search='collections']"
    );
    this.productColumn = this.querySelector("[js-instant-search='products']");
    this.articleColumn = this.querySelector("[js-instant-search='articles']");
    this.overlay = this.querySelector("[js-instant-search='overlay']");
    this.headerSearchButton = document.querySelector(
      "[js-header='mobile-search']"
    );
    this.updateOverlayMarginClass();
  }
  updateOverlayMarginClass() {
    const header = document.querySelector(
      ".header-background, .header-background-transparent"
    );
    if (!this.overlay || !header)
      return;
    this.overlay.classList.remove(
      "instant-search-results__overlay--no-margin",
      "instant-search-results__overlay--negative-margin"
    );
    if (header.classList.contains("header-background")) {
      this.overlay.classList.add("instant-search-results__overlay--no-margin");
    } else {
      this.overlay.classList.add(
        "instant-search-results__overlay--negative-margin"
      );
    }
  }
  setEventListeners() {
    var _a, _b, _c, _d, _e, _f;
    (_a = this.searchInput) == null ? void 0 : _a.addEventListener("input", () => this.handleSearchInput());
    (_b = this.searchInput) == null ? void 0 : _b.addEventListener("focusin", () => {
      var _a2, _b2, _c2;
      const openModals = document.querySelectorAll(
        ".modal.is-open, .drawer.is-open, .desktop-menu__item.is-open"
      );
      openModals.forEach((modal) => {
        if (modal.classList.contains("desktop-menu__item")) {
          modal.classList.remove("is-open");
          const desktopMenu = modal.closest("uwp-desktop-menu");
          if (desktopMenu == null ? void 0 : desktopMenu.closeAllDropdowns) {
            desktopMenu.closeAllDropdowns();
          }
        } else if (modal.classList.contains("modal") || modal.classList.contains("drawer")) {
          const hideMethod = modal.hideModal || modal.hideDrawer;
          if (hideMethod) {
            hideMethod.call(modal);
          }
        }
      });
      (_a2 = this.resultsParent) == null ? void 0 : _a2.classList.remove("visually-hidden");
      const value = ((_b2 = this.searchInput) == null ? void 0 : _b2.value) || "";
      this.getSearchResults(value);
      (_c2 = this.overlay) == null ? void 0 : _c2.classList.add("is-visible");
      this.updateOverlayMarginClass();
      document.body.classList.add("search-active");
      if (isMobile()) {
        this.classList.add("is-active");
      } else {
        lock(this);
      }
      document.dispatchEvent(new CustomEvent("predictive-search:visible"));
    });
    this.addEventListener("focusout", (event) => {
      var _a2, _b2, _c2;
      const target = event.relatedTarget;
      if (!target) {
        return;
      }
      const isFocusingInsideParent = ((_a2 = this.resultsParent) == null ? void 0 : _a2.contains(target)) || this.contains(target);
      if (isFocusingInsideParent) {
        return;
      }
      (_b2 = this.resultsParent) == null ? void 0 : _b2.classList.add("visually-hidden");
      if (this.classList.contains("search-input--desktop")) {
        unlock(this);
      }
      (_c2 = this.overlay) == null ? void 0 : _c2.classList.remove("is-visible");
      this.classList.remove("is-active");
      document.body.classList.remove("search-active");
    });
    (_c = this.searchButton) == null ? void 0 : _c.addEventListener("click", (clickEvent) => {
      if (!this.searchInput || !this.searchInput.value) {
        clickEvent.preventDefault();
        return;
      }
    });
    (_d = this.closeButton) == null ? void 0 : _d.addEventListener("click", () => {
      this.clearInput();
      document.body.classList.remove("search-active");
    });
    (_e = this.viewAllButton) == null ? void 0 : _e.addEventListener("click", () => {
      var _a2;
      const query = ((_a2 = this.searchInput) == null ? void 0 : _a2.value) || "";
      window.location.href = `${window.Shopify.routes.root}search?type=product&q=${query}`;
    });
    document.addEventListener("click", (event) => {
      var _a2, _b2, _c2, _d2;
      const clickedElement = event.target;
      let isClickOutside = !this.contains(clickedElement) || ((_a2 = this.overlay) == null ? void 0 : _a2.contains(clickedElement));
      if (clickedElement === this.headerSearchButton) {
        isClickOutside = false;
      }
      if (isClickOutside && ((_b2 = this.overlay) == null ? void 0 : _b2.classList.contains("is-visible"))) {
        (_c2 = this.resultsParent) == null ? void 0 : _c2.classList.add("visually-hidden");
        if (this.classList.contains("search-input--desktop")) {
          unlock(this);
        }
        (_d2 = this.overlay) == null ? void 0 : _d2.classList.remove("is-visible");
        this.classList.remove("is-active");
        document.body.classList.remove("search-active");
      }
    });
    (_f = this.headerSearchButton) == null ? void 0 : _f.addEventListener("click", () => {
      var _a2;
      if (!this.classList.contains("search-input--mobile")) {
        return;
      }
      (_a2 = this.searchInput) == null ? void 0 : _a2.focus();
      document.body.classList.add("search-active");
    });
  }
  /**
   * Renders the search results panel based on the query and results received.
   * It updates the visibility of result sections such as "waiting", "has
   * results", and "no results".
   *
   * @param {string|undefined} query - The search query.
   * @param {BoostInstantSearchResponse|undefined} results - The search results object.
   */
  renderResultsPanel(query, results) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i;
    if (!query || !results) {
      (_a = this.waitingResults) == null ? void 0 : _a.classList.remove("visually-hidden");
      (_b = this.hasResults) == null ? void 0 : _b.classList.add("visually-hidden");
      (_c = this.noResults) == null ? void 0 : _c.classList.add("visually-hidden");
      return;
    }
    if (query && !results.all_empty) {
      (_d = this.waitingResults) == null ? void 0 : _d.classList.add("visually-hidden");
      (_e = this.hasResults) == null ? void 0 : _e.classList.remove("visually-hidden");
      (_f = this.noResults) == null ? void 0 : _f.classList.add("visually-hidden");
    } else if (query && results.all_empty) {
      (_g = this.waitingResults) == null ? void 0 : _g.classList.remove("visually-hidden");
      (_h = this.hasResults) == null ? void 0 : _h.classList.add("visually-hidden");
      (_i = this.noResults) == null ? void 0 : _i.classList.remove("visually-hidden");
    }
  }
  changeButtonState() {
    if (!this.searchButton) {
      return;
    }
  }
  clearInput() {
    if (!this.searchInput) {
      return;
    }
    this.searchInput.value = "";
    this.handleSearchInput();
  }
  /**
   * Retrieves search results based on the given query.
   * It directly interacts with the SearchAPIQueryHandler to fetch data and
   * renders the results using `renderResultsPanel`.
   *
   * @param {string} query - The search query string to be used to fetch search results.
   */
  async getSearchResults(query) {
    if (!query.trim()) {
      this.renderResultsPanel("");
      return;
    }
    try {
      const results = await this.searchHandler.executeSearch(
        query
      );
      this.renderResultsPanel(query, results);
      this.renderSearchResults(results);
    } catch (error) {
      throw new Error(error);
    }
  }
  /**
   * Helper function to determine if a given results object is an instance of
   * SearchResults.
   * This method uses type guarding to check if the 'products' property is
   * present.
   *
   * @param {SearchAPISearchResults} results - The
   * results object to check.
   * @returns {boolean} True if the results object is a SearchResults, false
   * otherwise.
   */
  isSearchApiResponse(results) {
    return results.products !== void 0;
  }
  /**
   * Renders search results by creating and appending result elements to their respective columns.
   * It delegates to other methods for creating individual result elements.
   *
   *  results - The results to render, either instant or detailed search responses.
   */
  renderSearchResults(results) {
    this.clearExistingSearchResults();
    const createResultAnchor = (href, text) => {
      const element = document.createElement("a");
      element.classList.add("instant-search-results__result", "text-sm");
      element.href = href;
      element.textContent = text;
      return element;
    };
    const appendResults = (container, data, createElement) => {
      if (data.length) {
        container.classList.remove("visually-hidden");
        const resultsContainer = container.querySelector(
          ".instant-search-results__results"
        );
        data.forEach(
          (item) => resultsContainer.appendChild(createElement(item))
        );
      } else {
        container.classList.add("visually-hidden");
      }
    };
    if (this.isSearchApiResponse(results)) {
      if (this.productColumn) {
        appendResults(this.productColumn, results.products, (product) => {
          const anchor = createResultAnchor(
            `${window.Shopify.routes.root}products/${product.handle}`,
            ""
          );
          anchor.classList.add("instant-search-results__product");
          if (product.featured_image) {
            const img = document.createElement("img");
            img.src = product.featured_image.url;
            img.alt = product.title;
            img.width = 80;
            img.height = 80;
            img.classList.add("instant-search-results__product-image");
            anchor.appendChild(img);
          }
          const productMeta = document.createElement("div");
          productMeta.classList.add("instant-search-results__product-meta");
          productMeta.innerHTML = `
            <p class="
              instant-search-results__product-title
              heading-4
            ">
              ${product.title}
            </p>
  
            <p class="
              instant-search-results__product-price
              text-md-body
            ">
              ${window.themeVars.store.currency.symbol}${product.price}
            </p>
          `;
          anchor.appendChild(productMeta);
          return anchor;
        });
      }
    }
  }
  /**
   * Clears all existing search results from the DOM.
   * This method is typically called before rendering new search results to remove old ones.
   */
  clearExistingSearchResults() {
    if (this.suggestionColumn) {
      const suggestions = this.suggestionColumn.querySelector(
        ".instant-search-results__results"
      );
      suggestions.innerHTML = "";
    }
    if (this.collectionColumn) {
      const collections = this.collectionColumn.querySelector(
        ".instant-search-results__results"
      );
      collections.innerHTML = "";
    }
    if (this.productColumn) {
      const products = this.productColumn.querySelector(
        ".instant-search-results__results"
      );
      products.innerHTML = "";
    }
    if (this.articleColumn) {
      const articles = this.articleColumn.querySelector(
        ".instant-search-results__results"
      );
      articles.innerHTML = "";
    }
  }
  /**
   * Handles the input event on the search input field.
   * It synchronizes the search attribute across other inputs and updates the URL query.
   *
   */
  handleSearchInput() {
    var _a, _b, _c;
    const searchValue = ((_a = this.searchInput) == null ? void 0 : _a.value) || "";
    this.getSearchResults(searchValue);
    if (searchValue) {
      (_b = this.closeButton) == null ? void 0 : _b.classList.remove("is-hidden");
    } else {
      (_c = this.closeButton) == null ? void 0 : _c.classList.add("is-hidden");
    }
  }
  /**
   * Sets the initial search value based on the URL query parameters.
   * This method is called during the `connectedCallback` to initialize the search input with a potential query value from the URL.
   */
  setInitialValueFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("q");
    if (searchParam) {
      this.setAttribute("query", searchParam);
    }
  }
}
customElements.define("uwp-search-input", SearchInput);
